import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import SectionTitle from '../common/SectionTitle';
import { get } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import { IMAGE_BASE_URL } from '../../config';

const SponsorsSection = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const language = getUserLanguage(); 
  
  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const headers = { 'accept-language': language };
        const data = await get('/sponsors', headers);
        setSponsors(data);
      } catch (error) {
        console.error('Error fetching sponsors:', error);
        setError(language === 'cs' ? 'Nepodařilo se načíst sponzory' : 'Failed to load sponsors data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSponsors();
  }, [language]);
  
  if (loading) {
    return (
      <section id="sponsors" className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6">
          <SectionTitle
            badge={language === 'cs' ? 'Sponzoři' : 'Sponsors'}
            title={language === 'cs' ? 'Naši' : 'Our'}
            highlightedText={language === 'cs' ? 'partneři' : 'partners'}
            description={language === 'cs' ? 'Načítání...' : 'Loading...'}
          />
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
          </div>
        </div>
      </section>
    );
  }
  
  if (error) {
    return (
      <section id="sponsors" className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6">
          <SectionTitle
            badge={language === 'cs' ? 'Sponzoři' : 'Sponsors'}
            title={language === 'cs' ? 'Naši' : 'Our'}
            highlightedText={language === 'cs' ? 'partneři' : 'partners'}
            description={error}
          />
        </div>
      </section>
    );
  }
  
  const descriptions = {
    cs: 'Děkujeme našim partnerům za podporu, která nám umožňuje rozvíjet náš sport.',
    en: 'We thank our partners for their support, which allows us to grow our sport.'
  };

  // Group sponsors into rows based on the desired number of columns per row
  const groupSponsorsIntoRows = (sponsors, colsPerRow = 6) => {
    const rows = [];
    for (let i = 0; i < sponsors.length; i += colsPerRow) {
      rows.push(sponsors.slice(i, i + colsPerRow));
    }
    return rows;
  };

  // Determine how many columns per row based on screen size
  // These values should match the grid-cols in the responsive layout
  const getColsPerRow = () => {
    // For mobile (default): 2 per row
    // For md screens: 3 per row
    // For lg screens: 4 per row
    // For xl screens: 6 per row
    
    // We'll use 5 as our target to ensure centering works well across breakpoints
    return 5;
  };

  const sponsorRows = groupSponsorsIntoRows(sponsors, getColsPerRow());

  return (
    <section id="sponsors" className="py-20 bg-black" ref={sectionRef}>
      <div className="container mx-auto px-4 sm:px-6">
        <SectionTitle
          badge={language === 'cs' ? 'Sponzoři' : 'Sponsors'}
          title={language === 'cs' ? 'Naši' : 'Our'}
          highlightedText={language === 'cs' ? 'partneři' : 'partners'}
          description={descriptions[language] || descriptions.en}
        />
        
        {sponsorRows.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex justify-center mb-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
              {row.map((sponsor, index) => {
                const sponsorData = sponsor.translations && sponsor.translations[language]
                  ? sponsor.translations[language]
                  : (sponsor.translations && sponsor.translations.en
                    ? sponsor.translations.en
                    : { name: '' });
                return (
                  <div 
                    key={sponsor.id} 
                    className={`flex items-center justify-center p-6 bg-dark-gray/50 rounded-lg hover:bg-dark-gray transition-colors h-32 ${inView ? 'animate-fade-in' : 'opacity-0'}`}
                    style={{ 
                      animationDelay: `${0.1 * (index + rowIndex * getColsPerRow())}s`,
                    }}
                  >
                    <a 
                      href={sponsor.website_url || '#'} 
                      target={sponsor.website_url && sponsor.website_url !== '#' ? '_blank' : '_self'} 
                      rel="noopener"
                      className="flex items-center justify-center w-full h-full"
                    >
                      <div className="relative w-full h-full flex items-center justify-center">
                        <img 
                          src={sponsor.logo_path ? (import.meta.env.DEV ? sponsor.logo_path : `${IMAGE_BASE_URL}${sponsor.logo_path}`) : '/images/sponsors/placeholder.jpeg'} 
                          alt={sponsorData.name || (language === 'cs' ? 'Sponzor' : 'Sponsor')} 
                          className="max-w-full max-h-full object-contain transition-all duration-300"
                          style={{
                            filter: 'grayscale(100%)',
                            WebkitFilter: 'grayscale(100%)',
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.filter = 'grayscale(0%)';
                            e.currentTarget.style.WebkitFilter = 'grayscale(0%)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.filter = 'grayscale(100%)';
                            e.currentTarget.style.WebkitFilter = 'grayscale(100%)';
                          }}
                          loading="lazy"
                          onError={(e) => {
                            console.error(`Failed to load image: ${sponsor.logo_path}`);
                            e.target.onerror = null;
                            e.target.src = '/images/sponsors/placeholder.jpeg';
                          }}
                        />
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default SponsorsSection;